<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <div class="flex mb-6 w-full overflow-x-scroll">
            <draggable
                v-model="form.menu_items"
                class="flex flex-row"
                @choose="froalaStore.disableFroala = true"
                @unchoose="froalaStore.disableFroala = false"
            >
                <div
                    v-for="(item, index) in form.menu_items"
                    :key="`menu-item-${index}`"
                    class="bg-white rounded-lg shadow mr-4 p-4 cursor-pointer relative"
                    style="min-width: 240px"
                >
                    <menu-item
                        :item="item"
                        :limit="8"
                        :index="index"
                        @remove-item="removeItem(index)"
                        @add-document="addDocument(index)"
                        @add-parent="addParent(index)"
                        @remove-sub-item="removeSubItem"
                        @change-parent="replaceParent(index)"
                        @remove-parent="removeParent(index)"
                        :error="
                            errors[`menu_items.${index}.name`]
                                ? errors[`menu_items.${index}.name`][0]
                                : null
                        "
                    ></menu-item>
                </div>
            </draggable>
            <div
                class="flex flex-col rounded-lg border-2 border-gray-400 border-dashed p-6 items-center opacity-50 hover:opacity-100 cursor-pointer justify-center text-gray-400"
                @click="addMenuItem"
                style="min-width: 240px"
                v-if="form.menu_items.length < 4"
            >
                <svg
                    class="w-8 h-8 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                </svg>
                <p class="font-semibold">Click to add a new item</p>
            </div>
        </div>
        <slide-over
            :language="language"
            :is-open="slideOver.isOpen"
            @close="slideOver.isOpen = false"
            component="ModelList"
            :model="menuDocument"
            :excluded-documents="selectedItems"
            :list-route="contentTypes[menuDocument].listRoute"
            :label="contentTypes[menuDocument].columnSelector"
            :parent="
                filterParent
                    ? {
                          id: currentParent ? currentParent.id : null,
                          type: currentParent
                              ? currentParent.document_type ||
                                currentParent.model_name
                              : null,
                      }
                    : null
            "
            :query="{
                status_id: 1,
            }"
            @created="documentAdded"
            @selected="documentAdded"
        ></slide-over>

        <input-rich-text
            label="Text Above Logos"
            name="text_above_logos"
            v-model="form.text_above_logos"
            class="mt-2"
            :error="errors.text_above_logos ? errors.text_above_logos[0] : null"
            :maxlength=5000
            :language="language"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Logos</p>
            <multi-reference
                create-text="Create new logo and link to this footer"
                link-text="Link existing logos"
                :creatable="true"
                :linkable="false"
                :editable="true"
                :language="language"
                v-model="form.logos"
                field-name="logos"
                model-data="Footer Logo"
                :error="errors.logos ? errors.logos[0] : null"
            />
        </div>

        <input-rich-text
            label="Text Bottom"
            name="text_bottom"
            v-model="form.text_bottom"
            class="mt-2"
            :error="errors.text_bottom ? errors.text_bottom[0] : null"
            :maxlength=5000
            :language="language"
        />

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
    import crudForm from "../mixins/crudForm";
    import draggable from "vuedraggable";
    import EventBus from "../eventBus";
    import reference from "../mixins/reference";
    import menuBuilder from "../mixins/menu-builder";
    import siteLanguage from '../config/siteLanguage';
    import {useFroalaStore} from "../stores/froala";

    export default {
        name: "FooterForm",
        mixins: [crudForm, reference, menuBuilder],
        props: {
            language: {
                type: Object,
                default: () => {
                    return { iso: siteLanguage.iso.ENGLISH, id: siteLanguage.language.ENGLISH };
                },
            },
        },
        data() {
            return {
                form: {
                    text_above_logos: this.getFormData("text_above_logos"),
                    text_bottom: this.getFormData("text_bottom"),
                    logos: this.getFormData("logos"),
                    menu_items: this.getFormData("menu_items") ?? [],
                },

                slideOver: {
                    isOpen: false,
                },

                currentMenuItem: null,
                menuDocument: "Menu Document",
                isParent: false,
                froalaStore: useFroalaStore(),
            };
        },
        methods: {
            replaceParent(index) {
                this.isParent = true;
                this.addParent(index);
            },
            removeParent(index) {
                this.form.menu_items[index].document = null;
            },
            documentAdded(object) {
                if (this.isParent) {
                    this.addTopLevelParent(object);
                } else {
                    this.addDocumentAsItem(object);
                }
                this.isParent = false;
            },
            addTopLevelParent({ object: document }) {
                this.form.menu_items[this.currentMenuItem].document = document;
                this.form.menu_items[this.currentMenuItem].name =
                    document[this.contentTypes[this.documentType].columnSelector];
                this.slideOver.isOpen = false;
            },
            addMenuItem: function () {
                this.form.menu_items.push({
                    name: "",
                    document: null,
                    children: [],
                });
                this.$nextTick(() => {
                    EventBus.$emit("nameInputFocus", this.form.menu_items.length - 1);
                });
            },
            addDocumentAsItem: function ({ object: document }) {
                this.form.menu_items[this.currentMenuItem].children.push(document);
                this.slideOver.isOpen = false;
            },
            addDocument: function (menuItemIndex) {
                this.isParent = false;
                this.menuDocument = "Menu Document";
                this.currentMenuItem = menuItemIndex;
                this.slideOver.isOpen = true;
            },
            addParent: function (menuItemIndex) {
                this.isParent = true;
                this.menuDocument = "Menu Document";
                this.currentMenuItem = menuItemIndex;
                this.slideOver.isOpen = true;
            },
            removeItem: function (index) {
                const canDelete = confirm(
                    "Are you sure you want to remove this menu item?"
                );
                if (canDelete) {
                    this.form.menu_items.splice(index, 1);
                    this.currentMenuItem = null;
                }
            },
            removeSubItem: function ({ index, subIndex }) {
                const canDelete = confirm(
                    "Are you sure you want to remove this sub menu item?"
                );
                if (canDelete) {
                    this.form.menu_items[index].children.splice(subIndex, 1);
                }
            },
        },
        computed: {
            currentItem() {
                return this.form.menu_items[this.currentMenuItem];
            },
            currentParent() {
                if (this.currentMenuItem !== null) {
                    return this.currentItem.document || null;
                } else {
                    return null;
                }
            },
            filterParent() {
                //TEMPORARY DISABLING SO THE MENU CAN BE BUILT
                return false;
            },
            selectedItems() {
                if (!this.form.menu_items) return [];
                return this.form.menu_items
                    .map((item) => item.document || null)
                    .filter((item) => !!item)
                    .map((document) => {
                        return {
                            id: document.id,
                            document_type:
                                document.document_type || document.model_name,
                        };
                    });
            },
        },
        watch: {
            footer: {
                deep: true,
                handler: function () {
                    this.$emit("input", this.footer);
                },
            },
        },
};
</script>
